/* ══════════════════ Jungle Mode — Assumptions Bar + Decision Table ══════════════════ */

import React from 'react';
import type { JungleModeConfig, JungleModeStep, RouteDirection, AIStrategy } from './jungleModeTypes';

/* ── Helpers ── */

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Parse "m:ss" or raw seconds string → seconds */
function parseTime(val: string): number {
  if (val.includes(':')) {
    const [m, s] = val.split(':').map(Number);
    return (m || 0) * 60 + (s || 0);
  }
  return Number(val) || 0;
}

/* ── Props ── */

interface JungleModePanelProps {
  config: JungleModeConfig;
  onConfigChange: (partial: Partial<JungleModeConfig>) => void;
  steps: JungleModeStep[];
  onUndo: () => void;
  canUndo: boolean;
}

/* ── Component ── */

const JungleModePanel: React.FC<JungleModePanelProps> = ({
  config,
  onConfigChange,
  steps,
  onUndo,
  canUndo,
}) => {
  return (
    <div className="jm-panel">
      {/* ── Assumptions Bar ── */}
      <div className="jm-assumptions">
        <div className="jm-field">
          <label className="jm-label">Side</label>
          <select
            className="jm-select"
            value={config.playerSide}
            onChange={(e) => onConfigChange({ playerSide: e.target.value as 'blue' | 'red' })}
          >
            <option value="blue">Blue</option>
            <option value="red">Red</option>
          </select>
        </div>

        <div className="jm-field">
          <label className="jm-label">Route</label>
          <select
            className="jm-select"
            value={config.routeDirection}
            onChange={(e) => onConfigChange({ routeDirection: e.target.value as RouteDirection })}
          >
            <option value="top-to-bot">Top → Bot</option>
            <option value="bot-to-top">Bot → Top</option>
          </select>
        </div>

        <div className="jm-field">
          <label className="jm-label">Enemy</label>
          <select
            className="jm-select"
            value={config.aiStrategy}
            onChange={(e) => onConfigChange({ aiStrategy: e.target.value as AIStrategy })}
          >
            <option value="mirror">Mirror</option>
            <option value="top-to-bot">Top → Bot</option>
            <option value="bot-to-top">Bot → Top</option>
            <option value="random">Random</option>
          </select>
        </div>

        <div className="jm-divider" />

        <div className="jm-field">
          <label className="jm-label">Clear @</label>
          <input
            className="jm-input jm-input-time"
            type="text"
            value={fmtTime(config.clearFinishTime)}
            onChange={(e) => {
              const sec = parseTime(e.target.value);
              if (sec > 0) onConfigChange({ clearFinishTime: sec });
            }}
            title="Game time when full clear finishes (m:ss)"
          />
        </div>

        <div className="jm-field">
          <label className="jm-label">Scuttle @</label>
          <input
            className="jm-input jm-input-time"
            type="text"
            value={config.scuttleTime > 0 ? fmtTime(config.scuttleTime) : 'off'}
            onChange={(e) => {
              const val = e.target.value.toLowerCase();
              if (val === 'off' || val === '0') {
                onConfigChange({ scuttleTime: 0 });
              } else {
                const sec = parseTime(val);
                if (sec > 0) onConfigChange({ scuttleTime: sec });
              }
            }}
            title="Target arrival at scuttle crab (m:ss or 'off')"
          />
        </div>

        <div className="jm-field">
          <label className="jm-label">2nd+ Clear</label>
          <input
            className="jm-input"
            type="number"
            min={1}
            max={60}
            value={config.subsequentClearTime}
            onChange={(e) => onConfigChange({ subsequentClearTime: Number(e.target.value) || 10 })}
          />
          <span className="jm-unit">s</span>
        </div>

        <div className="jm-field">
          <label className="jm-label">Move Speed</label>
          <input
            className="jm-input"
            type="number"
            min={250}
            max={500}
            step={5}
            value={config.moveSpeed}
            onChange={(e) => onConfigChange({ moveSpeed: Number(e.target.value) || 350 })}
          />
        </div>

        <div className="jm-field">
          <label className="jm-label">Start</label>
          <input
            className="jm-input"
            type="number"
            min={0}
            max={600}
            step={5}
            value={config.startTime}
            onChange={(e) => onConfigChange({ startTime: Number(e.target.value) || 90 })}
          />
          <span className="jm-unit">s</span>
        </div>

        <div className="jm-spacer" />

        <button
          className="rsp-btn jm-undo-btn"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo last step"
        >
          ↩ Undo
        </button>
      </div>

      {/* ── Decision Table ── */}
      <div className="jm-table-wrap">
        <table className="jm-table">
          <thead>
            <tr>
              <th>Arrive</th>
              <th>Done</th>
              <th>#</th>
              <th>Camp</th>
              <th>Walk</th>
              <th>Clear</th>
              <th>Idle</th>
              <th>Gold</th>
              <th>XP</th>
              <th>Lvl</th>
              <th>Total Gold</th>
            </tr>
          </thead>
          <tbody>
            {steps.length === 0 && (
              <tr className="jm-empty-row">
                <td colSpan={11}>Click a camp on the map to begin your jungle path</td>
              </tr>
            )}
            {steps.map((step, i) => {
              if (step.isFreeTimeRow) {
                return (
                  <tr key={`free-${i}`} className="jm-free-time-row">
                    <td colSpan={11}>
                      <div className="jm-free-time-content">
                        <span className="jm-free-time-label">
                          Free Time: <b>{step.freeTime}s</b>
                        </span>
                        {step.freeTimeDetail && (
                          <span className="jm-free-time-detail">
                            ({step.freeTimeDetail})
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }

              // Check if this is the first step of a new cycle
              const prevStep = i > 0 ? steps[i - 1] : null;
              const showCycleHeader =
                prevStep && (prevStep.isFreeTimeRow || prevStep.cycleNumber !== step.cycleNumber);

              return (
                <React.Fragment key={`step-${i}`}>
                  {showCycleHeader && (
                    <tr className="jm-cycle-header">
                      <td colSpan={11}>Clear #{step.cycleNumber}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="jm-td-gametime">{fmtTime(step.gameTimeArrive)}</td>
                    <td className="jm-td-gametime">{fmtTime(step.gameTimeAfter)}</td>
                    <td className="jm-td-num">{step.stepNumber}</td>
                    <td className="jm-td-camp">{step.campName}</td>
                    <td className="jm-td-time">{step.walkTime}s</td>
                    <td className="jm-td-time">{step.clearTime > 0 ? `${step.clearTime}s` : '-'}</td>
                    <td className="jm-td-idle">{step.idleTime > 0 ? `${step.idleTime}s` : '-'}</td>
                    <td className="jm-td-gold">{step.gold > 0 ? `+${step.gold}` : '-'}</td>
                    <td className="jm-td-xp">{step.xp > 0 ? `+${step.xp}` : '-'}</td>
                    <td className="jm-td-lvl">{step.level}</td>
                    <td className="jm-td-gold">{step.cumulativeGold}g</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JungleModePanel;
